import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Sidebar.css';
import { logoutUser } from '../../api/api';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

type NavItem = {
  label: string;
  path: string;
  icon: React.ReactNode;
};

const iconProps = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  xmlns: 'http://www.w3.org/2000/svg',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function DashboardIcon() {
  return (
    <svg {...iconProps}>
      <rect x="3" y="3" width="7" height="7" rx="2" />
      <rect x="14" y="3" width="7" height="4" rx="2" />
      <rect x="14" y="10" width="7" height="11" rx="2" />
      <rect x="3" y="14" width="7" height="7" rx="2" />
    </svg>
  );
}

function ProfileIcon() {
  return (
    <svg {...iconProps}>
      <path d="M20 21a8 8 0 0 0-16 0" />
      <circle cx="12" cy="8" r="4" />
    </svg>
  );
}

function PromptsIcon() {
  return (
    <svg {...iconProps}>
      <path d="M4 5h16" />
      <path d="M4 12h10" />
      <path d="M4 19h16" />
      <circle cx="18" cy="12" r="2" />
    </svg>
  );
}

function MessagesIcon() {
  return (
    <svg {...iconProps}>
      <path d="M7 10h10" />
      <path d="M7 14h6" />
      <path d="M21 12c0 4.4-4 8-9 8-1.5 0-2.9-.3-4.2-.9L3 20l1.2-4.1A7.6 7.6 0 0 1 3 12c0-4.4 4-8 9-8s9 3.6 9 8Z" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg {...iconProps}>
      <path d="M8 2v4" />
      <path d="M16 2v4" />
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18" />
    </svg>
  );
}

function AnalyticsIcon() {
  return (
    <svg {...iconProps}>
      <path d="M4 20V10" />
      <path d="M10 20V4" />
      <path d="M16 20v-6" />
      <path d="M22 20v-9" />
    </svg>
  );
}

function NegotiationIcon() {
  return (
    <svg {...iconProps}>
      <path d="M12 2v4" />
      <path d="M12 18v4" />
      <path d="m4.93 4.93 2.83 2.83" />
      <path d="m16.24 16.24 2.83 2.83" />
      <path d="M2 12h4" />
      <path d="M18 12h4" />
      <path d="m4.93 19.07 2.83-2.83" />
      <path d="m16.24 7.76 2.83-2.83" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg {...iconProps}>
      <path d="M15 3h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-3" />
      <path d="M10 17l5-5-5-5" />
      <path d="M15 12H3" />
    </svg>
  );
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
  { label: 'Profile', path: '/user-profile', icon: <ProfileIcon /> },
  { label: 'Prompts', path: '/prompts', icon: <PromptsIcon /> },
  { label: 'Message Hub', path: '/messages', icon: <MessagesIcon /> },
  { label: 'Calendar', path: '/calendar', icon: <CalendarIcon /> },
  { label: 'Analytics', path: '/analytics', icon: <AnalyticsIcon /> },
  {
    label: 'Negotiation Center',
    path: '/dashboard/negotiator-center',
    icon: <NegotiationIcon />,
  },
];

function isNavActive(itemPath: string, pathname: string) {
  if (itemPath === '/prompts') {
    return pathname === '/prompts' || pathname.startsWith('/prompts/');
  }
  if (itemPath === '/messages') {
    return pathname === '/messages' || pathname.startsWith('/messages/');
  }
  if (itemPath === '/dashboard/negotiator-center') {
    return (
      pathname === '/dashboard/negotiator-center' ||
      pathname.startsWith('/dashboard/negotiator-center/')
    );
  }
  if (itemPath === '/dashboard') {
    return (
      pathname === '/dashboard' ||
      (pathname.startsWith('/dashboard/') && !pathname.startsWith('/dashboard/negotiator-center'))
    );
  }
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <aside
      className={`v2-sidebar ${isOpen ? 'v2-sidebar-open' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      <nav className="v2-sidebar-nav" aria-label="Main navigation">
        {NAV_ITEMS.map((item) => {
          const active = isNavActive(item.path, location.pathname);
          return (
            <button
              key={item.path}
              type="button"
              className={`v2-sidebar-item ${active ? 'v2-sidebar-item--active' : ''}`}
              onClick={() => handleNavigate(item.path)}
            >
              <span className="v2-sidebar-icon">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="v2-sidebar-footer">
        <button
          type="button"
          className="v2-sidebar-item v2-sidebar-item--logout"
          onClick={() => logoutUser()}
        >
          <span className="v2-sidebar-icon">
            <LogoutIcon />
          </span>
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
